<template>
    <section class="g-container || o-band">
        <div class="o-container">

            <app-intro class="u-d-flex align-items-center justify-content-between">
                <h3 class="u-mb-0">Storec gebruiker toevoegen</h3>

                <router-link to="/admin/storec-mobiel" class="c-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18.589" height="12.388" viewBox="0 0 18.589 12.388">
                        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
                            <path d="M17.589 6.223H4.714"/>
                            <path d="M7.439 10.989L1.001 6.195l6.438-4.8" stroke-linejoin="round"/>
                            <path d="M7.439 10.99L5.894 6.195l1.545-4.8" stroke-linejoin="round"/>
                        </g>
                    </svg>

                    Terug <span class="u-hidden-xsl-down">naar overzicht</span>
                </router-link>
            </app-intro>

            <div class="c-loading" v-if="loading">
                <spinner/>
                Bezig met laden van de gebruiker...
            </div>

            <transition-group name="fade" tag="section" v-if="!loading">
                <form v-on:submit.prevent="handleSubmit" class="form form---tint" novalidate key="form" autocomplete="off">
                    <div class="g-gap:2-xs g-gap:2-sm>sm || o-card">

                        <div class="form-fieldset">
                            <h4 class="u-mb-0">Gegevens</h4>

                            <div class="form-group" :class="{ 'has-error': $v.form.email.$error, 'has-success': submitted && !$v.form.email.$error }">
                                <label class="u-sr-only" for="login-email">Email</label>
                                <input type="email" v-model="form.email" id="login-email" name="Emailadres" class="form-control" placeholder="Vul hier het e-mailadres van de Storec gebruiker in" @keyup="checkEmail" />
                                <div v-if="$v.form.email.$error" class="form-validation">
                                    <span v-if="!$v.form.email.required">E-mail is verplicht</span>
                                    <span v-if="!$v.form.email.email">E-mail is ongeldig</span>
                                </div>
                                <div class="form-loader" v-if="status.checking">
                                    <spinner/>
                                </div>
                                <div class="form-validation || u-text-muted" v-if="disabled">Nadat het e-mailadres gecontroleerd is kun je verder</div>
                                <div class="form-validation || u-text-success" v-if="emailExists && $v.form.email.email">Het e-mailadres is gecontroleerd en is al bekend bij ons</div>
                                <div class="form-validation || u-text-danger" v-if="!emailExists && !disabled && !$v.form.email.$error">Het e-mailadres is gecontroleerd en is nog niet bekend bij ons, vul aanvullende informatie in</div>
                            </div>

                            <div class="form-group" :class="{ 'has-error': submitted && $v.form.name.$error, 'has-success': submitted && !$v.form.name.$error }">
                                <label class="u-sr-only" for="register-name">Naam</label>
                                <input type="text" v-model="form.name" id="register-name" name="Naam" class="form-control" placeholder="Vul hier de naam van de Storec gebruiker in" :disabled="disabled || $v.form.email.$error || emailExists" :readonly="emailExists" />
                                <div v-if="submitted && $v.form.name.$error" class="form-validation">
                                    <span v-if="!$v.form.name.required">Naam is verplicht</span>
                                </div>
                            </div>

                            <div class="form-group" :class="{ 'has-error': submitted && $v.form.initials.$error, 'has-success': submitted && !$v.form.initials.$error }">
                                <label class="u-sr-only" for="register-initials">Initialen</label>
                                <input type="text" v-model="form.initials" id="register-initials" name="DefaultInitialen" class="form-control" placeholder="Vul hier de initialen van de Storec gebruiker in" :disabled="disabled || $v.form.email.$error || emailExists" :readonly="emailExists" />
                                <div v-if="submitted && $v.form.initials.$error" class="form-validation">
                                    <span v-if="!$v.form.initials.required">Initialen zijn verplicht</span>
                                </div>
                            </div>

                            <div v-show="!emailExists" class="form-group" :class="{ 'has-error': submitted && $v.form.phone.$error, 'has-success': submitted && !$v.form.phone.$error }">
                                <label class="u-sr-only" for="register-phone">Telefoonnummer</label>
                                <input type="text" v-model="form.phone" id="register-phone" name="GSM" class="form-control" placeholder="Vul hier het telefoonnummer van de Storec gebruiker in" :disabled="disabled || $v.form.email.$error || emailExists" :readonly="emailExists" />
                                <div v-if="submitted && $v.form.phone.$error" class="form-validation">
                                    <span v-if="!$v.form.phone.required">Telefoonnummer is verplicht</span>
                                </div>
                            </div>
                        </div>

                        <div class="form-fieldset">
                            <h4 class="u-mb-0">Vestigingen</h4>

                            <div class="form-checkboxes" v-if="locations.length > 0">

                                <div class="custom-checkbox" v-for="(location, index) in locations" :key="location.VestigingID">
                                    <label :for="`location-${location.VestigingID}`" class="custom-control-label">
                                        <input type="checkbox" v-model="connectedLocations" :value="location.VestigingID" :name="`StorecMobielGebruikerVestigingen[${index}]`" :id="`location-${location.VestigingID}`" class="custom-control-input" />
                                        <span class="custom-control-name custom-control-name--reset">{{ location.Vestigingsnaam }}</span>
                                    </label>
                                </div>

                            </div>

                            <div v-else>
                                <p>Er zijn nog geen vestigingen aangemaakt. Maak er <router-link to="/admin/locations/add">hier</router-link> een aan.</p>
                            </div>
                        </div>
                    </div>

                    <button type="submit" class="[ c-btn c-btn--info ] form-submit-btn" :disabled="status.updating || status.adding || disabled">
                        <span v-show="status.updating || status.adding"><spinner/></span>
                        <span v-show="!status.updating && !status.adding">Gebruiker {{ emailExists ? 'wijzigen' : 'toevoegen'}}</span>
                        <svg v-show="!status.updating && !status.adding" xmlns="http://www.w3.org/2000/svg" width="14.875" height="14.875" viewBox="0 0 14.875 14.875">
                            <g fill="none" stroke="#fff" stroke-linecap="round" stroke-width="2">
                                <path d="M1 7.438h12.875"/>
                                <path d="M7.437 1v12.875"/>
                            </g>
                        </svg>
                    </button>
                </form>

            </transition-group>

        </div>
    </section>
</template>

<script>
    import { mapState } from 'vuex';
    import { required, email, minLength } from 'vuelidate/lib/validators';
    import AppIntro from '../../components/app/AppIntro.vue';
    import Spinner from '../../components/Spinner.vue';
    import normalizeFormData from '../../helpers/normalizeFormData';

    export default {
        components: {
            Spinner,
            AppIntro,
        },
        data () {
            return {
                loading: true,
                submitted: false,
                disabled: true,
                emailExists: false,
                connectedLocations: [],
                form: {
                    id: '',
                    email: '',
                    name: '',
                    initials: '',
                    phone: '',
                    locations: [],
                },
            };
        },
        computed: {
            ...mapState('storecMobiel', ['status']),
            ...mapState('location', {
                locations: 'list',
            }),
        },
        validations () {
            if (!this.emailExists)
            {
                return {
                    form: {
                        email: {
                            required,
                            email,
                        },
                        name: { required },
                        initials: { required },
                        phone: { required },
                    },
                };
            }

            if (this.emailExists)
            {
                return {
                    form: {
                        email: {
                            required,
                            email,
                        },
                        name: { minLength: minLength(1) },
                        initials: { minLength: minLength(1) },
                        phone: { minLength: minLength(1) },
                    },
                };
            }

            return false;
        },
        async created () {
            // get locations
            await this.$store.dispatch('location/list');

            // reset loading
            this.loading = false;
        },
        methods: {
            async checkEmail () {
                this.$v.form.$touch();

                if (this.$v.form.email.$error) return;

                // disable the fields
                this.disabled = true;
                this.emailExists = false;

                // check the email
                const result = await this.$store.dispatch('storecMobiel/checkEmail', this.form.email);

                // enable the fields
                this.disabled = false;

                // reset the values
                this.form.id = '';
                this.form.name = '';
                this.form.initials = '';
                this.form.phone = '';

                if (result !== false)
                {
                    this.emailExists = true;

                    // fill the fields
                    this.form.id = result.GebruikerMobielID;
                    this.form.name = result.Naam;
                    this.form.initials = result.DefaultInitialen;
                    this.form.phone = result.GSM;
                }
            },
            showModal ($event) {
                const { location, id } = $event.target.dataset;
                this.modal.id = id;
                this.modal.location = location;
                this.modal.visible = true;
            },
            closeModal () {
                this.modal.location = '';
                this.modal.visible = false;
            },
            addSubscription ($event) {
                console.log($event);
            },
            handleSubmit (e) {
                this.submitted = true;

                // validate the form
                this.$v.form.$touch();

                // if its still pending or an error is returned do not submit
                if (this.$v.form.$pending || this.$v.form.$error) return;

                // normalize form data
                const data = normalizeFormData(e.target);

                // kick off the request
                if (this.emailExists)
                {
                    // get the id
                    const id = this.form.id;

                    this.$store.dispatch('storecMobiel/updateUser', { id, data });
                }
                else
                {
                    this.$store.dispatch('storecMobiel/createUser', { data });
                }
            },
        },

    };
</script>

<style lang="scss" scoped>
    @import "../../assets/scss/_essentials.scss";

    /*.table {
        @include media-breakpoint-only(sm) {
            grid-template-columns: rem(150) 1fr;
            margin: 0;
            max-width: 100%;
        }
    }*/
</style>
